// Shared frontmatter validation. Used by:
// - src/docs/manifest.ts (runtime, on first import via Vite)
// - scripts/validate-docs.mjs (pre-build, via tsx, independent of Vite)
//
// Any new validation rule MUST be added here so both consumers enforce it
// in lockstep — never add a check to one side only.

// Relative imports here (not @/...) so the validate-docs.mjs Node script
// can run this file via tsx without a path-alias resolver.
import { MENU_REGISTRY, submenuFeatureFlag } from "../constants/menuRegistry";
import { DOC_GROUPS } from "./docGroups";
import type { DocFrontmatter } from "../types/docs";
import type { HelpTopicFrontmatter } from "../types/help";

// Only "main"-surface, docs-eligible modules are valid docs `module:` keys.
// Settings-surface sections (e.g. "settings") drive per-account nav visibility,
// not docs gating — allowing `module: settings` would let an article gate on a
// non-docs surface. `docsEligible: false` sections (e.g. "dashboard") are
// gateable landing pages with no docs, so they're excluded too.
const DOCS_MODULES = MENU_REGISTRY.filter(
  (m) => m.surface !== "settings" && m.docsEligible !== false
);
const VALID_MODULE_KEYS = new Set(DOCS_MODULES.map((m) => m.key));
const VALID_SUBMENU_KEYS = new Map<string, Set<string>>(
  DOCS_MODULES.map((m) => [m.key, new Set(m.submenus.map((s) => s.key))])
);

/**
 * Universal sections never gate on `module`/`submenu`. Articles in these
 * folders MUST omit those frontmatter fields — gating them would defeat
 * the empty-state guarantee from the plan ("docs sidebar is never fully
 * empty").
 */
export const UNIVERSAL_SECTIONS = new Set([
  "getting-started",
  "account-admin",
  "troubleshooting",
]);

/** Display labels of the universal sections (module sections use MENU_REGISTRY). */
export const UNIVERSAL_SECTION_LABELS: Record<string, string> = {
  "getting-started": "Getting started",
  "account-admin": "Account admin",
  troubleshooting: "Troubleshooting",
};

const FEATURE_FLAG_RE = /^[a-z][a-z0-9_]{0,63}$/;

/**
 * `publicSummary` is published on ask-verity.com (plan
 * 2026-10-13-docs-llm-versions-signed-in-and-public): plain text, feature
 * level, never how. No markup that could carry a link or a component.
 */
export const PUBLIC_SUMMARY_MAX = 400;
const PUBLIC_SUMMARY_MARKUP_RE = /[<>{}`]|\]\(/;
const PRODUCT_NAME_TAG_RE = /<\s*ProductName\s*\/?\s*>/g;

/**
 * `kind` labels the article on its metadata line ("Updated … · 4 min read ·
 * Connection guide"): short plain text, no markup at all — unlike
 * `publicSummary`, not even `<ProductName />`.
 */
export const KIND_MAX = 40;

function validateKind(slug: string, f: Record<string, unknown>): void {
  if (f.kind === undefined) return;
  const v = f.kind;
  if (typeof v !== "string" || v.trim() === "") {
    throw new Error(`[docs] ${slug}: frontmatter.kind must be a non-empty string.`);
  }
  if (v.length > KIND_MAX) {
    throw new Error(`[docs] ${slug}: frontmatter.kind is ${v.length} characters; at most ${KIND_MAX}.`);
  }
  if (PUBLIC_SUMMARY_MARKUP_RE.test(v)) {
    throw new Error(`[docs] ${slug}: frontmatter.kind is plain text — no tags, {, }, backticks or Markdown links.`);
  }
}

function validatePublicSummary(slug: string, f: Record<string, unknown>): void {
  if (f.publicSummary === undefined) return;
  const v = f.publicSummary;
  if (typeof v !== "string" || v.trim() === "") {
    throw new Error(`[docs] ${slug}: frontmatter.publicSummary must be a non-empty string.`);
  }
  if (v.length > PUBLIC_SUMMARY_MAX) {
    throw new Error(`[docs] ${slug}: frontmatter.publicSummary is ${v.length} characters; at most ${PUBLIC_SUMMARY_MAX}.`);
  }
  // `<ProductName />` is the one allowed tag: frontmatter ships in the app
  // bundle on partner domains too, so the brand is never written literally.
  if (PUBLIC_SUMMARY_MARKUP_RE.test(v.replace(PRODUCT_NAME_TAG_RE, ""))) {
    throw new Error(`[docs] ${slug}: frontmatter.publicSummary is public plain text — no markup except <ProductName />: no other tags, {, }, backticks or Markdown links.`);
  }
  // A flagged feature is built, not released: it is never advertised. The
  // submenu's flag (MENU_REGISTRY) counts as much as the article's own.
  const submenuFlag =
    typeof f.module === "string" && typeof f.submenu === "string"
      ? submenuFeatureFlag(f.module, f.submenu)
      : undefined;
  const flag = (typeof f.featureFlag === "string" && f.featureFlag) || submenuFlag;
  if (flag) {
    throw new Error(
      `[docs] ${slug}: frontmatter.publicSummary is not allowed on a flag-gated feature ("${flag}") — it would advertise an unreleased feature on the public site.`
    );
  }
}

/**
 * At most one public summary per feature: per submenu (or per module without
 * a submenu). Universal-section articles may each carry one. Takes every
 * docs article (help excluded) with its frontmatter.
 */
export function validatePublicSummaries(
  articles: Iterable<{ slug: string; fm: DocFrontmatter }>
): void {
  const byFeature = new Map<string, string[]>();
  for (const { slug, fm } of articles) {
    if (fm.publicSummary === undefined || !fm.module) continue;
    const key = fm.submenu ? `${fm.module}/${fm.submenu}` : fm.module;
    byFeature.set(key, [...(byFeature.get(key) ?? []), slug]);
  }
  const doubled = [...byFeature].filter(([, slugs]) => slugs.length > 1);
  if (doubled.length > 0) {
    throw new Error(
      `[docs] more than one publicSummary per feature: ${doubled
        .map(([k, slugs]) => `${k} (${slugs.join(", ")})`)
        .join("; ")} — keep one, on the article that introduces the feature.`
    );
  }
}

const VALID_ACCOUNT_ROLES = new Set([
  "OWNER",
  "ADMIN",
  "MEMBER",
  "VIEWER",
  "SUPPORT",
]);

/**
 * Validate a single article's frontmatter. Throws on the first violation
 * with a descriptive message including the slug — the failure messages
 * are surfaced by both the build-time validator script and Vite's HMR
 * overlay in dev, so authors see the exact problem.
 */
export function validateFrontmatter(
  slug: string,
  fm: unknown
): asserts fm is DocFrontmatter {
  if (!fm || typeof fm !== "object") {
    throw new Error(
      `[docs] ${slug}: missing frontmatter export. Check that remark-mdx-frontmatter is configured in vite.config.mts and that the article begins with a --- ... --- block.`
    );
  }
  const f = fm as Record<string, unknown>;

  if (typeof f.title !== "string" || f.title.trim() === "") {
    throw new Error(`[docs] ${slug}: frontmatter.title is required and must be a non-empty string.`);
  }
  if (typeof f.description !== "string" || f.description.trim() === "") {
    throw new Error(`[docs] ${slug}: frontmatter.description is required and must be a non-empty string.`);
  }

  const section = slug.split("/")[0];
  const isUniversal = UNIVERSAL_SECTIONS.has(section);

  if (isUniversal && (f.module !== undefined || f.submenu !== undefined)) {
    throw new Error(
      `[docs] ${slug}: universal-section articles (${section}/) must NOT have module/submenu — universal sections are always visible. Found module="${String(f.module)}" submenu="${String(f.submenu)}".`
    );
  }

  if (f.module !== undefined) {
    if (typeof f.module !== "string") {
      throw new Error(`[docs] ${slug}: frontmatter.module must be a string.`);
    }
    if (!VALID_MODULE_KEYS.has(f.module)) {
      throw new Error(
        `[docs] ${slug}: unknown module "${f.module}". Valid keys: ${[...VALID_MODULE_KEYS].join(", ")} (see src/constants/menuRegistry.ts).`
      );
    }
    if (f.submenu !== undefined) {
      if (typeof f.submenu !== "string") {
        throw new Error(`[docs] ${slug}: frontmatter.submenu must be a string.`);
      }
      const submenus = VALID_SUBMENU_KEYS.get(f.module);
      if (!submenus?.has(f.submenu)) {
        throw new Error(
          `[docs] ${slug}: unknown submenu "${f.submenu}" for module "${f.module}". Valid: ${[...(submenus ?? [])].join(", ") || "(none)"}.`
        );
      }
    }
  } else if (f.submenu !== undefined) {
    throw new Error(`[docs] ${slug}: frontmatter.submenu requires frontmatter.module to also be set.`);
  }

  if (f.roles !== undefined) {
    if (!Array.isArray(f.roles)) {
      throw new Error(`[docs] ${slug}: frontmatter.roles must be an array of AccountRole strings.`);
    }
    for (const role of f.roles) {
      if (typeof role !== "string" || !VALID_ACCOUNT_ROLES.has(role)) {
        throw new Error(
          `[docs] ${slug}: invalid role "${String(role)}". Valid: ${[...VALID_ACCOUNT_ROLES].join(", ")}.`
        );
      }
    }
  }

  if (f.featureFlag !== undefined) {
    if (typeof f.featureFlag !== "string" || !FEATURE_FLAG_RE.test(f.featureFlag)) {
      throw new Error(`[docs] ${slug}: frontmatter.featureFlag must be a feature-flag key (lowercase letters, digits, _).`);
    }
    if (!f.module) {
      throw new Error(`[docs] ${slug}: frontmatter.featureFlag needs a module (universal content is never flag-gated).`);
    }
  }

  if (f.order !== undefined && typeof f.order !== "number") {
    throw new Error(`[docs] ${slug}: frontmatter.order must be a number.`);
  }

  validatePublicSummary(slug, f);
  validateKind(slug, f);

  if (f.updated !== undefined) {
    if (typeof f.updated !== "string") {
      throw new Error(`[docs] ${slug}: frontmatter.updated must be a string.`);
    }
    if (Number.isNaN(new Date(f.updated).getTime())) {
      throw new Error(`[docs] ${slug}: frontmatter.updated "${f.updated}" is not a valid ISO date.`);
    }
  }
}

/**
 * Validate where an article lives (plan 2026-10-13-docs-sidebar-groups):
 *   <section>/<article>                    — universal, or a module article without submenu
 *   <section>/<submenu>/<article>          — ungrouped, folder === frontmatter.submenu
 *   <section>/<submenu>/<group>/<article>  — grouped, group registered in DOC_GROUPS
 * Nothing deeper. Help topics (`_help/`) are not articles and never come here.
 * Call after `validateFrontmatter`, from both the manifest and validate-docs.
 */
export function validateDocLocation(slug: string, fm: DocFrontmatter): void {
  const parts = slug.split("/");
  if (parts.length > 4) {
    throw new Error(
      `[docs] ${slug}: too deep — an article lives at <section>/<submenu>/<group>/<article> at most.`
    );
  }
  if (parts.length >= 3 && parts[1] !== fm.submenu) {
    throw new Error(
      `[docs] ${slug}: folder "${parts[1]}" must equal frontmatter.submenu (found "${String(fm.submenu)}") — a subfolder of a section is its submenu.`
    );
  }
  if (parts.length === 3 && DOC_GROUPS[slug]) {
    throw new Error(
      `[docs] ${slug}: this slug is a group folder (an index.mdx inside "${parts[2]}/"?). Give the article its own file name.`
    );
  }
  if (parts.length === 4) {
    const key = parts.slice(0, 3).join("/");
    if (!DOC_GROUPS[key]) {
      throw new Error(
        `[docs] ${slug}: group folder "${key}" is not registered — add it to DOC_GROUPS in src/docs/docGroups.ts with a label and order.`
      );
    }
  }
}

/**
 * Every DOC_GROUPS entry must hold at least one article — a label without
 * articles is a dead registry row. Takes all docs slugs (help excluded).
 */
export function validateGroupRegistry(slugs: Iterable<string>): void {
  const used = new Set<string>();
  for (const slug of slugs) {
    const parts = slug.split("/");
    if (parts.length === 4) used.add(parts.slice(0, 3).join("/"));
  }
  const dead = Object.keys(DOC_GROUPS).filter((k) => !used.has(k));
  if (dead.length > 0) {
    throw new Error(
      `[docs] DOC_GROUPS entries without any article: ${dead.join(", ")} — remove them from src/docs/docGroups.ts or add the folder.`
    );
  }
}

/**
 * Validate a single help-topic's frontmatter (the drawer-only `_help/`
 * surface — see ADR-0069). Same shared rules as `validateFrontmatter`,
 * with one inversion: `module` is REQUIRED (and `submenu` is required when
 * the module has submenus, forbidden when it doesn't), because every help
 * topic is page-attached — there is no universal help. Used by both the
 * runtime help manifest and the build-time validate-docs script in
 * lockstep, exactly like `validateFrontmatter` for docs.
 */
export function validateHelpFrontmatter(
  key: string,
  fm: unknown
): asserts fm is HelpTopicFrontmatter {
  if (!fm || typeof fm !== "object") {
    throw new Error(
      `[help] ${key}: missing frontmatter export. Check that the topic begins with a --- ... --- block.`
    );
  }
  const f = fm as Record<string, unknown>;

  if (typeof f.title !== "string" || f.title.trim() === "") {
    throw new Error(`[help] ${key}: frontmatter.title is required and must be a non-empty string.`);
  }
  if (typeof f.description !== "string" || f.description.trim() === "") {
    throw new Error(`[help] ${key}: frontmatter.description is required and must be a non-empty string.`);
  }

  // module is REQUIRED for help topics (page-attached, never universal).
  if (typeof f.module !== "string" || f.module.trim() === "") {
    throw new Error(
      `[help] ${key}: frontmatter.module is required — help topics are always page-attached (no universal help). Valid keys: ${[...VALID_MODULE_KEYS].join(", ")}.`
    );
  }
  if (!VALID_MODULE_KEYS.has(f.module)) {
    throw new Error(
      `[help] ${key}: unknown module "${f.module}". Valid keys: ${[...VALID_MODULE_KEYS].join(", ")} (see src/constants/menuRegistry.ts).`
    );
  }

  const submenus = VALID_SUBMENU_KEYS.get(f.module);
  const moduleHasSubmenus = (submenus?.size ?? 0) > 0;
  if (moduleHasSubmenus) {
    if (typeof f.submenu !== "string" || f.submenu.trim() === "") {
      throw new Error(
        `[help] ${key}: module "${f.module}" has submenus, so frontmatter.submenu is required. Valid: ${[...(submenus ?? [])].join(", ")}.`
      );
    }
    if (!submenus?.has(f.submenu)) {
      throw new Error(
        `[help] ${key}: unknown submenu "${f.submenu}" for module "${f.module}". Valid: ${[...(submenus ?? [])].join(", ")}.`
      );
    }
  } else if (f.submenu !== undefined) {
    throw new Error(
      `[help] ${key}: module "${f.module}" has no submenus, so frontmatter.submenu must be omitted (found "${String(f.submenu)}").`
    );
  }

  if (f.roles !== undefined) {
    if (!Array.isArray(f.roles)) {
      throw new Error(`[help] ${key}: frontmatter.roles must be an array of AccountRole strings.`);
    }
    for (const role of f.roles) {
      if (typeof role !== "string" || !VALID_ACCOUNT_ROLES.has(role)) {
        throw new Error(
          `[help] ${key}: invalid role "${String(role)}". Valid: ${[...VALID_ACCOUNT_ROLES].join(", ")}.`
        );
      }
    }
  }

  if (f.featureFlag !== undefined) {
    if (typeof f.featureFlag !== "string" || !FEATURE_FLAG_RE.test(f.featureFlag)) {
      throw new Error(`[help] ${key}: frontmatter.featureFlag must be a feature-flag key (lowercase letters, digits, _).`);
    }
    if (!f.module) {
      throw new Error(`[help] ${key}: frontmatter.featureFlag needs a module (universal content is never flag-gated).`);
    }
  }

  if (f.order !== undefined && typeof f.order !== "number") {
    throw new Error(`[help] ${key}: frontmatter.order must be a number.`);
  }

  if (f.publicSummary !== undefined) {
    throw new Error(`[help] ${key}: frontmatter.publicSummary is for articles only — help topics are never public.`);
  }

  if (f.updated !== undefined) {
    if (typeof f.updated !== "string") {
      throw new Error(`[help] ${key}: frontmatter.updated must be a string.`);
    }
    if (Number.isNaN(new Date(f.updated).getTime())) {
      throw new Error(`[help] ${key}: frontmatter.updated "${f.updated}" is not a valid ISO date.`);
    }
  }
}
